// Presentaciones (kits): tarjetas + constructor de receta + fabricar/deshacer.
// Port de views/presentaciones.js.
import { useState } from 'react';
import { usePresentaciones, useCrearPresentacion, useEditarPresentacion, useEliminarPresentacion } from '../hooks/usePresentaciones';
import { useProductos } from '../hooks/useProductos';
import { useBodegas } from '../hooks/useBodegas';
import { idBodegaPrincipal } from '../lib/api/bodegas';
import { useAgregarStock, useDescontarStock } from '../hooks/useInventario';
import { Badge } from '../components/Badge';
import { Modal } from '../components/Modal';
import { Field } from '../components/Field';
import { SkeletonItems } from '../components/Skeletons';
import { ErrorBox } from '../components/ErrorBox';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/Confirm';
import type { Bodega, Presentacion, Producto } from '../types';

export function Presentaciones() {
  const { data: presentaciones, isLoading, isError, error } = usePresentaciones();
  const { data: productos } = useProductos();
  const { data: bodegas } = useBodegas();
  const [form, setForm] = useState<{ open: boolean; pres?: Presentacion }>({ open: false });
  const [fab, setFab] = useState<{ pres: Presentacion; modo: 'fabricar' | 'deshacer' } | null>(null);
  const eliminar = useEliminarPresentacion();
  const toast = useToast();
  const confirm = useConfirm();

  const abrirNueva = () => {
    if (!(productos ?? []).length) { toast.warning('Sin productos', 'Crea productos antes de armar una presentación.'); return; }
    setForm({ open: true, pres: undefined });
  };

  const borrar = async (p: Presentacion) => {
    const ok = await confirm({ title: 'Eliminar presentación', message: `¿Eliminar la definición de "${p.nombre}"?` });
    if (!ok) return;
    try {
      await eliminar.mutateAsync(p.id);
      toast.success('Presentación eliminada');
    } catch (e) {
      toast.error('No se pudo eliminar', (e as Error).message);
    }
  };

  return (
    <>
      <div className="page-head">
        <h2>Presentaciones</h2>
        <button className="btn primary" onClick={abrirNueva}><i className="fa-solid fa-plus" /> Nueva presentación</button>
      </div>
      <p className="text-muted" style={{ marginTop: -8, marginBottom: 18 }}>
        Los kits no tienen stock propio: fabricarlos consume sus componentes y deshacerlos los devuelve al inventario.
      </p>

      {isLoading ? (
        <SkeletonItems n={6} />
      ) : isError ? (
        <ErrorBox mensaje={(error as Error).message} />
      ) : (presentaciones ?? []).length ? (
        <div className="grid cols-3">
          {(presentaciones ?? []).map((p) => {
            const comps = p.componentes ?? [];
            const total = p.stockTotal ?? 0;
            return (
              <div className="card item-card hoverable" key={p.id}>
                <div className="title"><span className="chip c-success" style={{ width: 34, height: 34 }}><i className="fa-solid fa-boxes-stacked" /></span>{p.nombre}</div>
                <div className="meta">{p.descripcion || 'Sin descripción'}</div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span><Badge type={total > 0 ? 'success' : 'neutral'}>{total} fabricadas</Badge></span>
                  <span className="text-muted" style={{ fontSize: 12 }}>{comps.length} componente(s)</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  {comps.slice(0, 4).map((c) => (
                    <div className="row" key={c.detalle_id ?? c.producto_id} style={{ justifyContent: 'space-between' }}>
                      <span>{c.producto_nombre}</span><b>×{c.cantidad}</b>
                    </div>
                  ))}
                  {comps.length > 4 ? <small>+{comps.length - 4} más…</small> : null}
                </div>
                <div className="actions">
                  <button className="btn success sm" onClick={() => setFab({ pres: p, modo: 'fabricar' })}><i className="fa-solid fa-industry" /> Fabricar</button>
                  <button className="btn secondary sm" onClick={() => setFab({ pres: p, modo: 'deshacer' })}><i className="fa-solid fa-rotate-left" /> Deshacer</button>
                  <button className="btn ghost sm icon-only" onClick={() => setForm({ open: true, pres: p })}><i className="fa-solid fa-pen" /></button>
                  <button className="btn ghost sm icon-only" onClick={() => borrar(p)}><i className="fa-solid fa-trash" /></button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid cols-3">
          <div className="card" style={{ gridColumn: '1/-1' }}>
            <div className="empty-state"><i className="fa-solid fa-boxes-stacked" />No hay presentaciones. Crea la primera combinando varios productos.</div>
          </div>
        </div>
      )}

      {form.open ? <PresentacionModal pres={form.pres} productos={productos ?? []} onClose={() => setForm({ open: false })} /> : null}
      {fab ? <FabricarModal pres={fab.pres} modo={fab.modo} bodegas={bodegas ?? []} onClose={() => setFab(null)} /> : null}
    </>
  );
}

interface CompRow { key: number; producto_id: string; cantidad: string; }

function PresentacionModal({ pres, productos, onClose }: { pres?: Presentacion; productos: Producto[]; onClose: () => void }) {
  const editar = !!pres;
  const crear = useCrearPresentacion();
  const editarM = useEditarPresentacion();
  const toast = useToast();
  const [nombre, setNombre] = useState(pres?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(pres?.descripcion ?? '');
  const iniciales: CompRow[] = (editar && (pres!.componentes ?? []).length ? pres!.componentes! : [{ producto_id: productos[0]?.id ?? '', cantidad: 1 }])
    .map((c, i) => ({ key: i, producto_id: c.producto_id || productos[0]?.id || '', cantidad: String(c.cantidad ?? 1) }));
  const [comps, setComps] = useState<CompRow[]>(iniciales);
  const [enviado, setEnviado] = useState(false);
  const [nextKey, setNextKey] = useState(iniciales.length);
  const cargando = crear.isPending || editarM.isPending;

  const addRow = () => { setComps((cs) => [...cs, { key: nextKey, producto_id: productos[0]?.id ?? '', cantidad: '1' }]); setNextKey((k) => k + 1); };
  const rmRow = (key: number) => setComps((cs) => cs.filter((c) => c.key !== key));
  const setRow = (key: number, patch: Partial<CompRow>) => setComps((cs) => cs.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const guardar = async () => {
    setEnviado(true);
    if (!nombre.trim()) return;
    const componentes = comps
      .map((c) => ({ producto_id: c.producto_id, cantidad: Number(c.cantidad) }))
      .filter((c) => c.producto_id && c.cantidad > 0);
    if (!componentes.length) { toast.warning('Falta la receta', 'Añade al menos un componente.'); return; }
    const input = { nombre, descripcion, componentes };
    try {
      if (editar) await editarM.mutateAsync({ id: pres!.id, input });
      else await crear.mutateAsync(input);
      toast.success(editar ? 'Presentación actualizada' : 'Presentación creada', nombre);
      onClose();
    } catch (e) {
      toast.error('No se pudo guardar', (e as Error).message);
    }
  };

  return (
    <Modal
      open size="lg"
      title={editar ? 'Editar presentación' : 'Nueva presentación'}
      icon="fa-boxes-stacked" iconColor="success"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className={`btn success${cargando ? ' loading' : ''}`} onClick={guardar}>{editar ? 'Guardar cambios' : 'Crear presentación'}</button>
        </>
      }
    >
      <Field name="nombre" label="Nombre" required value={nombre} onChange={setNombre} invalid={enviado && !nombre.trim()} />
      <Field name="descripcion" label="Descripción" type="textarea" value={descripcion} onChange={setDescripcion} />
      <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)' }}>Componentes (receta) *</label>
      <div style={{ marginTop: 8 }}>
        {comps.map((c) => (
          <div className="componente-row" key={c.key}>
            <div className="field">
              <label>Producto</label>
              <select className="control" value={c.producto_id} onChange={(e) => setRow(c.key, { producto_id: e.target.value })}>
                {productos.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Cant.</label>
              <input type="number" min={1} className="control" value={c.cantidad} onChange={(e) => setRow(c.key, { cantidad: e.target.value })} />
            </div>
            <button type="button" className="btn ghost icon-only" title="Quitar" style={{ marginBottom: 2 }} onClick={() => rmRow(c.key)}><i className="fa-solid fa-xmark" /></button>
          </div>
        ))}
      </div>
      <button type="button" className="btn outline sm" onClick={addRow}><i className="fa-solid fa-plus" /> Añadir componente</button>
    </Modal>
  );
}

function FabricarModal({ pres, modo, bodegas, onClose }: { pres: Presentacion; modo: 'fabricar' | 'deshacer'; bodegas: Bodega[]; onClose: () => void }) {
  const esFab = modo === 'fabricar';
  const agregar = useAgregarStock();
  const descontar = useDescontarStock();
  const toast = useToast();
  const [bodegaId, setBodegaId] = useState(idBodegaPrincipal(bodegas));
  const [cantidad, setCantidad] = useState('1');
  const [enviado, setEnviado] = useState(false);
  const cargando = agregar.isPending || descontar.isPending;

  if (!bodegas.length) {
    return (
      <Modal open title={esFab ? 'Fabricar' : 'Deshacer'} icon="fa-industry" onClose={onClose}
        footer={<button className="btn ghost" onClick={onClose}>Cerrar</button>}>
        <p className="text-muted" style={{ margin: 0 }}>Crea una bodega primero.</p>
      </Modal>
    );
  }

  const aplicar = async () => {
    setEnviado(true);
    const cant = Number(cantidad);
    if (!bodegaId || !cant || cant <= 0) return;
    const op = { bodegaId, tipo: 'presentacion' as const, refId: pres.id, cantidad: cant };
    try {
      if (esFab) await agregar.mutateAsync(op);
      else await descontar.mutateAsync(op);
      toast.success(esFab ? 'Presentaciones fabricadas' : 'Presentaciones deshechas', pres.nombre);
      onClose();
    } catch (e) {
      toast.error('Operación no permitida', (e as Error).message);
    }
  };

  return (
    <Modal
      open
      title={`${esFab ? 'Fabricar' : 'Deshacer'} — ${pres.nombre}`}
      icon={esFab ? 'fa-industry' : 'fa-rotate-left'} iconColor={esFab ? 'success' : 'warning'}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className={`btn ${esFab ? 'success' : 'warning'}${cargando ? ' loading' : ''}`} onClick={aplicar}>{esFab ? 'Fabricar' : 'Deshacer'}</button>
        </>
      }
    >
      <p className="text-muted" style={{ marginTop: 0 }}>
        {esFab ? 'Se descontarán los componentes de la bodega elegida.' : 'Se devolverán los componentes al inventario de la bodega.'}
      </p>
      <Field name="bodegaId" label="Bodega" type="select" required value={bodegaId} onChange={setBodegaId}
        options={bodegas.map((b) => ({ value: b.id, label: b.principal ? `${b.nombre} (principal)` : b.nombre }))} invalid={enviado && !bodegaId} />
      <Field name="cantidad" label="Cantidad de unidades" type="number" required min={1} value={cantidad} onChange={setCantidad}
        invalid={enviado && (!cantidad || Number(cantidad) <= 0)} />
    </Modal>
  );
}
